import React from 'react';
import { createRoot } from 'react-dom/client';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, Layout, ErrorBoundary } from '@/packages/application';
import { LandingPage } from '@/packages/marketing';
import { useCurrentUser } from '@/packages/shared/hooks/useCurrentUser';
import { setupGlobalErrorHandlers } from '@/packages/shared/utils/globalErrorHandlers';
import { queryClient } from '@/packages/shared/lib/queryClient';
import { initializeSentry } from '@/packages/shared/lib/sentry';
import { appUrl } from '@/packages/shared/utils/subdomainUrls';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../packages/shared/styles/globals.css';

function ExternalRedirect({ to }: { to: string }) {
  React.useEffect(() => {
    window.location.href = to;
  }, [to]);

  return <div>Redirecting to app...</div>;
}

function HomePageSelector() {
  const { data: currentUser, isLoading } = useCurrentUser();

  if (isLoading) {
    return <div>Loading user...</div>;
  }

  if (currentUser?.email) {
    // Redirect to app subdomain
    return <ExternalRedirect to={appUrl()} />;
  }

  return <LandingPage />;
}

function AuthRedirect() {
  // Get the current auth path and redirect to app subdomain
  const currentPath = window.location.pathname + window.location.search;
  return <ExternalRedirect to={appUrl(currentPath)} />;
}

initializeSentry();

setupGlobalErrorHandlers();

const root = createRoot(document.getElementById('marketing-app')!);
root.render(
  <ErrorBoundary boundaryName="Marketing">
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Notifications position="top-right" />
        <BrowserRouter>
          <Layout>
            <Routes>
              <Route path="/" element={<HomePageSelector />} />
              {/* Redirect all auth routes to app subdomain */}
              <Route path="/auth/*" element={<AuthRedirect />} />
              {/* Redirect any other paths to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);
